/**
 * useCreditBalance — encapsulates all AI credit balance concerns for ChatApp.
 *
 * Owns:
 *  - useWallet (fetch, state, refresh)
 *  - Broadcasting porter-credit-status to xplan-apex on state change
 *  - Re-fetching when the panel is shown again (porter-panel-shown event)
 *  - interceptCreditLimit: short-circuits handleSubmit when balance is exhausted
 *  - handleDismissCreditLimit: hides the CreditLimitToast for a specific turn
 *  - handleSeeUsage: dispatches porter-open-usage (Metering team pop-up, TBD)
 *
 * Extracting these here reduces ChatApp's cognitive complexity.
 */
import { useCallback, useEffect } from 'react';
import { useWallet } from '@/lib/useWallet';
import type { CreditState, WalletData } from '@/lib/wallet';

export interface CreditLimitTurn {
  id: number;
  query: string;
  status: 'credit_limit';
  dismissedCreditLimit: boolean;
}

export interface UseCreditBalanceResult {
  creditState: CreditState;
  walletData: WalletData | null;
  refreshWallet: () => void;
  /**
   * When exhausted: returns a new credit_limit turn to add to the transcript
   * (the caller calls `setTurns` and returns early). Returns null otherwise.
   */
  interceptCreditLimit: (message: string, nextId: () => number) => CreditLimitTurn | null;
  /**
   * Produces a `setTurns` updater that marks the given turn's toast dismissed.
   * Usage: `setTurns(dismissCreditLimitUpdate(id))`
   */
  dismissCreditLimitUpdate: (id: number) => <T extends { id: number }>(prev: T[]) => T[];
  /** Triggers the Metering team's usage pop-up (integration TBD). */
  handleSeeUsage: () => void;
}

export function useCreditBalance(): UseCreditBalanceResult {
  const { creditState, walletData, refresh: refreshWallet } = useWallet();

  // ── Broadcast credit state to xplan-apex header ────────────────────────
  useEffect(() => {
    const el = document.querySelector('xplan-porter');
    el?.dispatchEvent(
      new CustomEvent('porter-credit-status', {
        detail: { creditState },
        bubbles: true,
      }),
    );
  }, [creditState]);

  // ── Re-fetch when panel is shown again (future-proof) ─────────────────
  useEffect(() => {
    const handler = () => refreshWallet();
    document.addEventListener('porter-panel-shown', handler);
    return () => document.removeEventListener('porter-panel-shown', handler);
  }, [refreshWallet]);

  const interceptCreditLimit = useCallback(
    (message: string, nextId: () => number): CreditLimitTurn | null => {
      if (creditState !== 'exhausted') return null;
      return {
        id: nextId(),
        query: message,
        status: 'credit_limit',
        dismissedCreditLimit: false,
      };
    },
    [creditState],
  );

  const dismissCreditLimitUpdate = useCallback(
    (id: number) =>
      <T extends { id: number }>(prev: T[]): T[] =>
        prev.map((t) => (t.id === id ? { ...t, dismissedCreditLimit: true } : t)),
    [],
  );

  const handleSeeUsage = useCallback(() => {
    document.querySelector('xplan-porter')?.dispatchEvent(
      new CustomEvent('porter-open-usage', { bubbles: true }),
    );
  }, []);

  return {
    creditState,
    walletData,
    refreshWallet,
    interceptCreditLimit,
    dismissCreditLimitUpdate,
    handleSeeUsage,
  };
}
